import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import {
  LayoutDashboard, FileText, Users, Building2, Package, Database,
  Settings, LogOut, Menu, X, BarChart3, ShieldCheck, ChevronDown, Plus, Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: null },
  { to: "/offerte", label: "Offerte", icon: FileText, roles: null },
  { to: "/clienti", label: "Clienti", icon: Building2, roles: null },
  { to: "/followups", label: "Follow-up", icon: Bell, roles: null },
  { to: "/listino", label: "Listino servizi", icon: Package, roles: ["admin", "direzione"] },
  { to: "/pacchetti", label: "Pacchetti", icon: ShieldCheck, roles: ["admin", "direzione"] },
  { to: "/ateco", label: "Tabella ATECO", icon: Database, roles: ["admin", "direzione"] },
  { to: "/report", label: "Report", icon: BarChart3, roles: ["admin", "direzione"] },
  { to: "/utenti", label: "Utenti", icon: Users, roles: ["admin"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#0F2C59] text-white flex-col z-30">
        <div className="h-20 px-6 flex items-center gap-3 border-b border-white/10">
          <div className="w-12 h-12 rounded-md bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
            <img src="/csc-logo.jpg" alt="CSC" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight">PreventivatoreCSC</div>
            <div className="text-xs text-white/60 leading-tight">Centro Sicurezza Consulenze</div>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.to.replace("/", "") || "dashboard"}`}
              className={({ isActive }) =>
                `mx-3 my-0.5 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white/10 text-white border-l-4 border-[#FACC15] pl-2"
                    : "text-white/75 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <it.icon className="w-5 h-5" strokeWidth={2} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 font-mono uppercase tracking-wider mb-2">Sessione</div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#FACC15] text-[#0F2C59] flex items-center justify-center font-bold text-sm">
              {(user?.name || "?").substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-white/60">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded hover:bg-white/10 text-white/70 hover:text-white"
              title="Esci"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 bg-[#0F2C59] text-white flex flex-col animate-fade-in">
            <div className="h-16 px-4 flex items-center justify-between border-b border-white/10">
              <div className="font-display font-bold">PreventivatoreCSC</div>
              <button onClick={() => setMobileOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 py-3 overflow-y-auto">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.to === "/"} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `mx-3 my-0.5 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive ? "bg-white/10 text-white" : "text-white/75"
                    }`}>
                  <it.icon className="w-5 h-5" />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
            <div className="hidden lg:block">
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">Area operativa</div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button
                data-testid="new-offer-btn"
                onClick={() => navigate("/offerte/nuova")}
                className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0F2C59] font-semibold shadow-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Nuova offerta
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-2 p-1.5 rounded-md hover:bg-slate-100">
                    <div className="w-8 h-8 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-xs font-semibold">
                      {(user?.name || "?").substring(0, 2).toUpperCase()}
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm">
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-xs text-slate-500">{user?.email}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" /> Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8 max-w-[1600px] mx-auto animate-fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
